/**
 * Performance monitoring and Web Vitals tracking
 *
 * Tracks Core Web Vitals and custom performance metrics:
 * - CLS (Cumulative Layout Shift)
 * - FID (First Input Delay)
 * - LCP (Largest Contentful Paint)
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 *
 * Based on web-vitals library patterns
 */

export interface PerformanceMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta?: number;
  id?: string;
  navigationType?: string;
}

// Type augmentation for LayoutShift entries
interface LayoutShiftEntry extends PerformanceEntry {
  hadRecentInput: boolean;
  value: number;
}

// Type augmentation for Network Information API
interface NetworkInformation {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
}

// Type for PerformanceObserver init with durationThreshold
interface EventObserverInit extends PerformanceObserverInit {
  durationThreshold?: number;
}

// Type for PostHog global
interface PostHogInstance {
  capture: (eventName: string, properties: Record<string, unknown>) => void;
}

interface WindowWithPostHog extends Window {
  posthog?: PostHogInstance;
}

export interface PerformanceReport {
  url: string;
  timestamp: number;
  metrics: PerformanceMetric[];
  userAgent: string;
  connection?: {
    effectiveType?: string;
    rtt?: number;
    downlink?: number;
  };
}

type MetricCallback = (metric: PerformanceMetric) => void;

// Rating thresholds based on Web Vitals recommendations
const THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 },
  FID: { good: 100, poor: 300 },
  LCP: { good: 2500, poor: 4000 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 }, // Interaction to Next Paint
};

/**
 * Determine performance rating based on metric value and thresholds
 */
function getRating(
  metricName: keyof typeof THRESHOLDS,
  value: number
): "good" | "needs-improvement" | "poor" {
  const threshold = THRESHOLDS[metricName];
  if (value <= threshold.good) return "good";
  if (value <= threshold.poor) return "needs-improvement";
  return "poor";
}

/**
 * Track Cumulative Layout Shift (CLS)
 * Measures visual stability
 */
export function onCLS(callback: MetricCallback): void {
  if (!("LayoutShift" in window)) {
    console.warn("LayoutShift API not supported");
    return;
  }

  let clsValue = 0;
  const clsEntries: PerformanceEntry[] = [];

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      // Only count layout shifts without recent user input
      const layoutShiftEntry = entry as LayoutShiftEntry;
      if (!layoutShiftEntry.hadRecentInput) {
        clsValue += layoutShiftEntry.value;
        clsEntries.push(entry);
      }
    }
  });

  observer.observe({ type: "layout-shift", buffered: true });

  // Report CLS when page is hidden or unloaded
  const reportCLS = () => {
    callback({
      name: "CLS",
      value: clsValue,
      rating: getRating("CLS", clsValue),
    });
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      reportCLS();
    }
  });

  window.addEventListener("pagehide", reportCLS);
}

/**
 * Track First Input Delay (FID)
 * Measures interactivity
 */
export function onFID(callback: MetricCallback): void {
  if (!("PerformanceEventTiming" in window)) {
    console.warn("PerformanceEventTiming API not supported");
    return;
  }

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const fidEntry = entry as PerformanceEventTiming;
      callback({
        name: "FID",
        value: fidEntry.processingStart - fidEntry.startTime,
        rating: getRating("FID", fidEntry.processingStart - fidEntry.startTime),
      });
    }
  });

  observer.observe({ type: "first-input", buffered: true });
}

/**
 * Track Largest Contentful Paint (LCP)
 * Measures loading performance
 */
export function onLCP(callback: MetricCallback): void {
  if (!("LargestContentfulPaint" in window)) {
    console.warn("LargestContentfulPaint API not supported");
    return;
  }

  let lcpValue = 0;

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1] as PerformanceEntry;
    lcpValue = lastEntry.startTime;
  });

  observer.observe({ type: "largest-contentful-paint", buffered: true });

  // Report LCP when page is hidden
  const reportLCP = () => {
    callback({
      name: "LCP",
      value: lcpValue,
      rating: getRating("LCP", lcpValue),
    });
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      reportLCP();
    }
  });
}

/**
 * Track First Contentful Paint (FCP)
 * Measures initial rendering
 */
export function onFCP(callback: MetricCallback): void {
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === "first-contentful-paint") {
        callback({
          name: "FCP",
          value: entry.startTime,
          rating: getRating("FCP", entry.startTime),
        });
        observer.disconnect();
      }
    }
  });

  observer.observe({ type: "paint", buffered: true });
}

/**
 * Track Time to First Byte (TTFB)
 * Measures server response time
 */
export function onTTFB(callback: MetricCallback): void {
  const navigationEntry = performance.getEntriesByType(
    "navigation"
  )[0] as PerformanceNavigationTiming;

  if (!navigationEntry) {
    console.warn("Navigation timing not available");
    return;
  }

  const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;

  callback({
    name: "TTFB",
    value: ttfb,
    rating: getRating("TTFB", ttfb),
  });
}

/**
 * Track Interaction to Next Paint (INP)
 * Measures runtime responsiveness
 */
export function onINP(callback: MetricCallback): void {
  if (!("PerformanceEventTiming" in window)) {
    console.warn("PerformanceEventTiming API not supported");
    return;
  }

  let worstInp = 0;

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const eventEntry = entry as PerformanceEventTiming;
      const duration = eventEntry.processingEnd - eventEntry.startTime;

      if (duration > worstInp) {
        worstInp = duration;
      }
    }
  });

  observer.observe({
    type: "event",
    buffered: true,
    durationThreshold: 16, // Only track interactions > 16ms
  } as EventObserverInit);

  // Report INP when page is hidden
  const reportINP = () => {
    if (worstInp > 0) {
      callback({
        name: "INP",
        value: worstInp,
        rating: getRating("INP", worstInp),
      });
    }
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      reportINP();
    }
  });
}

/**
 * Initialize all Web Vitals tracking
 */
export function initWebVitals(onMetric?: MetricCallback): void {
  const metrics: PerformanceMetric[] = [];

  const collectMetric = (metric: PerformanceMetric) => {
    metrics.push(metric);
    console.log(`[Performance] ${metric.name}:`, metric.value, `(${metric.rating})`);
    onMetric?.(metric);
  };

  // Track all Core Web Vitals
  onCLS(collectMetric);
  onFID(collectMetric);
  onLCP(collectMetric);
  onFCP(collectMetric);
  onTTFB(collectMetric);
  onINP(collectMetric);

  // Report to analytics when page is hidden
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && metrics.length > 0) {
      reportToAnalytics(metrics);
    }
  });
}

/**
 * Report metrics to analytics service
 */
export function reportToAnalytics(metrics: PerformanceMetric[]): void {
  const report: PerformanceReport = {
    url: window.location.href,
    timestamp: Date.now(),
    metrics,
    userAgent: navigator.userAgent,
  };

  // Add connection information if available
  if ("connection" in navigator) {
    const conn = (navigator as NavigatorWithConnection).connection;
    if (conn) {
      report.connection = {
        effectiveType: conn.effectiveType,
        rtt: conn.rtt,
        downlink: conn.downlink,
      };
    }
  }

  // Send to analytics endpoint (customize based on your analytics provider)
  if (process.env.NODE_ENV === "production") {
    // Example: Send to PostHog, Google Analytics, or custom endpoint
    try {
      // PostHog example
      if (typeof window !== "undefined" && (window as WindowWithPostHog).posthog) {
        (window as WindowWithPostHog).posthog!.capture("web_vitals", report as unknown as Record<string, unknown>);
      }

      // You can also use Beacon API for reliable delivery
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(report)], {
          type: "application/json",
        });
        navigator.sendBeacon("/api/analytics/performance", blob);
      }
    } catch (error) {
      console.error("Failed to report performance metrics:", error);
    }
  } else {
    console.log("[Performance Report]", report);
  }
}

/**
 * Performance marks and measures helpers
 * Use for custom performance tracking
 */
export const performanceMark = {
  /**
   * Create a performance mark
   */
  start(name: string): void {
    if (typeof performance !== "undefined" && performance.mark) {
      performance.mark(`${name}-start`);
    }
  },

  /**
   * End a performance mark and measure duration
   */
  end(name: string): number | null {
    if (typeof performance === "undefined") return null;

    const startMark = `${name}-start`;
    const endMark = `${name}-end`;

    performance.mark(endMark);

    try {
      const measure = performance.measure(name, startMark, endMark);
      console.log(`[Performance] ${name}:`, measure.duration.toFixed(2), "ms");
      return measure.duration;
    } catch (error) {
      console.error(`Failed to measure ${name}:`, error);
      return null;
    }
  },

  /**
   * Clear specific performance marks
   */
  clear(name: string): void {
    if (typeof performance !== "undefined") {
      performance.clearMarks(`${name}-start`);
      performance.clearMarks(`${name}-end`);
      performance.clearMeasures(name);
    }
  },
};

/**
 * Track component render time
 * Use in React components to measure render performance
 */
export function trackComponentRender(componentName: string): {
  start: () => void;
  end: () => void;
} {
  return {
    start: () => performanceMark.start(componentName),
    end: () => performanceMark.end(componentName),
  };
}

/**
 * Track route change performance
 */
export function trackRouteChange(routeName: string): void {
  performanceMark.start(`route-${routeName}`);

  // Measure when route is fully rendered (after next frame)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      performanceMark.end(`route-${routeName}`);
    });
  });
}

/**
 * Track API call performance
 */
export function trackAPICall(endpoint: string): {
  start: () => void;
  end: () => void;
} {
  return {
    start: () => performanceMark.start(`api-${endpoint}`),
    end: () => performanceMark.end(`api-${endpoint}`),
  };
}

/**
 * Get current performance metrics summary
 */
export function getPerformanceMetrics(): {
  navigation: PerformanceNavigationTiming | null;
  resources: PerformanceResourceTiming[];
  marks: PerformanceMark[];
  measures: PerformanceMeasure[];
} {
  if (typeof performance === "undefined") {
    return {
      navigation: null,
      resources: [],
      marks: [],
      measures: [],
    };
  }

  return {
    navigation: performance.getEntriesByType(
      "navigation"
    )[0] as PerformanceNavigationTiming | null,
    resources: performance.getEntriesByType(
      "resource"
    ) as PerformanceResourceTiming[],
    marks: performance.getEntriesByType("mark") as PerformanceMark[],
    measures: performance.getEntriesByType("measure") as PerformanceMeasure[],
  };
}

/**
 * Log performance summary to console
 * Useful for debugging performance issues
 */
export function logPerformanceSummary(): void {
  const metrics = getPerformanceMetrics();

  console.group("Performance Summary");

  if (metrics.navigation) {
    const nav = metrics.navigation;
    console.log("Navigation Timing:");
    console.log(`  DNS Lookup: ${(nav.domainLookupEnd - nav.domainLookupStart).toFixed(2)}ms`);
    console.log(`  TCP Connection: ${(nav.connectEnd - nav.connectStart).toFixed(2)}ms`);
    console.log(`  Request/Response: ${(nav.responseEnd - nav.requestStart).toFixed(2)}ms`);
    console.log(`  DOM Processing: ${(nav.domComplete - nav.domContentLoadedEventStart).toFixed(2)}ms`);
    console.log(`  Page Load: ${(nav.loadEventEnd - nav.loadEventStart).toFixed(2)}ms`);
    console.log(`  Total: ${nav.loadEventEnd.toFixed(2)}ms`);
  }

  console.log("\nCustom Measures:");
  metrics.measures.forEach((measure) => {
    console.log(`  ${measure.name}: ${measure.duration.toFixed(2)}ms`);
  });

  console.log("\nResource Summary:");
  const resources = metrics.resources.reduce(
    (acc, resource) => {
      const type = resource.initiatorType || "other";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );
  console.table(resources);

  console.groupEnd();
}

export default {
  initWebVitals,
  onCLS,
  onFID,
  onLCP,
  onFCP,
  onTTFB,
  onINP,
  reportToAnalytics,
  performanceMark,
  trackComponentRender,
  trackRouteChange,
  trackAPICall,
  getPerformanceMetrics,
  logPerformanceSummary,
};
